import {useMemo, useState} from 'react'
import {Card, Spinner, Row, Col, Button} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import {TWILIO_ACCOUNT_USAGE, TWILIO_MONTH_USAGE} from '../../gql/queries/twilioBilling'

type Category = {category: string; description: string; usage: string; usage_unit: string; price: number; kind?: string}
type TrendPoint = {month: string; total: number}
type Usage = {
  this_month_total: number
  last_month_total: number
  balance: number | null
  currency: string
  categories: Category[]
  trend: TrendPoint[]
}

const money = (n: number | null | undefined) => {
  if (n == null) return '—'
  const v = Number(n)
  const abs = Math.abs(v).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
  return v < 0 ? `-$${abs}` : `$${abs}`
}

// Plain-language explanation for each Twilio category (keyed by the stable
// category code, since the friendly name is often opaque — e.g. "Channels").
const CATEGORY_HELP: Record<string, string> = {
  'phonenumbers-local': 'Monthly rental of your local phone numbers',
  'phonenumbers-tollfree': 'Monthly rental of your toll-free numbers',
  'channels': 'Twilio Conversations / Programmable Chat messages (omnichannel messaging)',
  'channels-messaging': 'Conversations messaging platform fee',
  'channels-messaging-inbound': 'Inbound messages through Conversations channels',
  'channels-messaging-outbound': 'Outbound messages through Conversations channels',
  'sms-outbound-longcode': 'Outbound SMS sent from your 10-digit numbers',
  'sms-inbound-longcode': 'Inbound SMS received on your numbers',
  'sms-messages-carrierfees': 'Carrier pass-through fees charged on each SMS',
  'bundle-sms-bucket': 'Prepaid US SMS bundle (bucket pricing)',
  'trunking-termination': 'Outbound call minutes over the SIP trunk (VICIdial dialing)',
  'trunking-origination-local': 'Inbound call minutes over the SIP trunk',
  'trunking-origination': 'Inbound call minutes over the SIP trunk',
  'a2p-registration-fees': 'A2P 10DLC messaging registration',
  'a2p-10dlc-registrationfees-campaigncharges': 'A2P 10DLC campaign registration fee',
  'a2p-10dlc-registrationfees-monthly': 'A2P 10DLC monthly registration fee',
  'mms-inbound': 'Inbound MMS (picture messages)',
  'mms-messages-carrierfees': 'Carrier pass-through fees charged on each MMS',
  'transcriptions': 'Voice call transcriptions',
  'calls-inbound-local': 'Inbound voice call minutes',
  'recordingstorage': 'Monthly storage of call recordings',
  'calls-recordings': 'Call recording minutes',
  'enhanced-line-type-lookups': 'Line Type Intelligence phone lookups',
  'failed-message-processing-fee': 'Fee for messages that failed to send',
  '__other__': 'Smaller categories grouped together',
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const formatMonth = (key: string) => {
  if (!key) return ''
  const [y, m] = key.split('-')
  return `${MONTHS[parseInt(m, 10) - 1] ?? m} ${y}`
}

// Billing → Cost & Usage: Twilio account spend overview for the organization.
// (Number inventory + "Add to pool" live in the Inventory numbers tab.)
export const TwilioBillingPage = () => {
  const {data: uData, loading: uLoading, error: uError} = useQuery(TWILIO_ACCOUNT_USAGE, {fetchPolicy: 'cache-and-network'})
  const usage: Usage | undefined = uData?.twilioAccountUsage

  const mom = useMemo(() => {
    if (!usage || !usage.last_month_total) return null
    return ((usage.this_month_total - usage.last_month_total) / usage.last_month_total) * 100
  }, [usage])
  const trendMax = useMemo(() => Math.max(1, ...(usage?.trend ?? []).map((t) => t.total)), [usage])

  // Drill-down: null = current month (live data already loaded); a 'YYYY-MM'
  // key = a past month fetched on demand.
  const [selMonth, setSelMonth] = useState<string | null>(null)
  const [selY, selM] = (selMonth ?? '0-0').split('-').map(Number)
  const {data: mData, loading: mLoading} = useQuery(TWILIO_MONTH_USAGE, {
    variables: {year: selY, month: selM},
    skip: !selMonth,
    fetchPolicy: 'cache-first',
  })

  if (uError) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-2'>Twilio billing unavailable</h4>
          <p className='text-muted mb-0'>{uError.message}</p>
        </Card.Body>
      </Card>
    )
  }

  const lowBalance = usage?.balance != null && usage.balance < 50

  // The month whose breakdown is shown on the right. Defaults to the current
  // month (last trend bucket); clicking a bar in "Monthly spend" drills in.
  const trend = usage?.trend ?? []
  const currentKey = trend.length ? trend[trend.length - 1].month : ''
  const monthUsage = selMonth ? mData?.twilioMonthUsage : null
  const activeCats: Category[] = (selMonth ? monthUsage?.categories : usage?.categories) ?? []
  const activeTotal = selMonth ? monthUsage?.total ?? 0 : usage?.this_month_total ?? 0
  const activeKey = selMonth ?? currentKey
  const activeLoading = selMonth ? mLoading && !monthUsage : uLoading && !usage
  const selectMonth = (key: string) => setSelMonth(key === currentKey ? null : key)

  // Build a clean tabular PDF report of the active month's breakdown using jsPDF
  // directly (no autotable dependency) — it's a simple 4-column invoice table.
  // Loaded on demand so the ~300KB lib isn't in the page's initial bundle.
  const exportPdf = async () => {
    const lineItems = activeCats.filter((c) => c.kind !== 'adjustment')
    const adjustment = activeCats.find((c) => c.kind === 'adjustment')
    const subtotal = lineItems.reduce((s, c) => s + c.price, 0)
    const period = formatMonth(activeKey)

    const {jsPDF} = await import('jspdf')
    const doc = new jsPDF({unit: 'pt', format: 'a4'})
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const left = 40
    const right = pageW - 40
    const cX = {cat: left, what: 170, usage: 372}
    const wrapW = {cat: 122, what: 194, usage: 78}
    let y = 100

    const fmt = (n: number) => {
      const v = Number(n)
      const a = Math.abs(v).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
      return v < 0 ? `-$${a}` : `$${a}`
    }

    const header = () => {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(25)
      doc.text('Twilio Usage & Billing', left, 50)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(120)
      doc.text(period, left, 68)
      doc.setFontSize(9)
      doc.text(`Generated ${new Date().toLocaleDateString('en-US')}`, right, 46, {align: 'right'})
      if (usage?.balance != null) doc.text(`Account balance: ${fmt(usage.balance)}`, right, 62, {align: 'right'})
      doc.setDrawColor(220); doc.line(left, 80, right, 80)
      y = 100
    }

    const tableHead = () => {
      doc.setFillColor(245, 246, 248)
      doc.rect(left, y - 12, right - left, 20, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(90)
      doc.text('CATEGORY', cX.cat + 2, y + 2)
      doc.text('WHAT IT IS', cX.what, y + 2)
      doc.text('USAGE', cX.usage, y + 2)
      doc.text('COST', right - 2, y + 2, {align: 'right'})
      y += 20
    }

    const row = (cat: string, what: string, use: string, cost: string, o: {bold?: boolean; green?: boolean} = {}) => {
      doc.setFontSize(9)
      doc.setFont('helvetica', o.bold ? 'bold' : 'normal')
      const catL = doc.splitTextToSize(cat, wrapW.cat)
      const whatL = doc.splitTextToSize(what || '', wrapW.what)
      const useL = doc.splitTextToSize(use || '', wrapW.usage)
      const n = Math.max(catL.length, whatL.length, useL.length, 1)
      const rowH = n * 11 + 6
      if (y + rowH > pageH - 60) { doc.addPage(); header(); tableHead() }
      const ty = y + 8
      doc.setTextColor(o.bold ? 25 : 45)
      doc.text(catL, cX.cat + 2, ty)
      doc.setTextColor(130)
      doc.text(whatL, cX.what, ty)
      doc.text(useL, cX.usage, ty)
      if (o.green) doc.setTextColor(25, 135, 84)
      else doc.setTextColor(o.bold ? 25 : 45)
      doc.text(cost, right - 2, ty, {align: 'right'})
      doc.setDrawColor(238); doc.line(left, y + rowH - 2, right, y + rowH - 2)
      y += rowH
    }

    header()
    tableHead()
    lineItems.forEach((c) =>
      row(c.description || c.category, CATEGORY_HELP[c.category] || '', `${c.usage} ${c.usage_unit}`.trim(), fmt(c.price))
    )

    y += 6
    doc.setDrawColor(180); doc.line(left, y - 2, right, y - 2)
    row('Subtotal', 'Usage at Twilio list price', '', fmt(subtotal))
    if (adjustment)
      row('Discount & adjustments', 'Your Twilio account discount (not itemized by Twilio)', '', fmt(adjustment.price), {green: true})
    doc.setDrawColor(120); doc.line(left, y, right, y); y += 4
    row('Total billed', 'What Twilio actually charges', '', fmt(activeTotal), {bold: true})

    y += 16
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(140)
    const note = doc.splitTextToSize(
      "Each category is shown at Twilio's standard (list) price. Your account discount is applied to the subtotal, so the Total billed is what Twilio actually charges for the period.",
      right - left
    )
    doc.text(note, left, y)

    doc.save(`twilio-billing-${period.replace(/\s+/g, '-')}.pdf`)
  }

  return (
    <div className='mt-5'>
      <div className='mb-5'>
        <h2 className='mb-1'>Billing</h2>
        <span className='text-muted'>Twilio account spend for the organization.</span>
      </div>

      <Row className='g-4 mb-2'>
        <Col md={4} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>This month</div>
            <div className='fs-2 fw-bold'>{uLoading && !usage ? <Spinner size='sm' animation='border' /> : money(usage?.this_month_total)}</div>
            {mom != null && (
              <span className={`badge ${mom > 0 ? 'badge-light-danger' : 'badge-light-success'}`}>
                {mom > 0 ? '▲' : '▼'} {Math.abs(mom).toFixed(0)}% vs last month
              </span>
            )}
          </Card.Body></Card>
        </Col>
        <Col md={4} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>Last month</div>
            <div className='fs-2 fw-bold'>{money(usage?.last_month_total)}</div>
          </Card.Body></Card>
        </Col>
        <Col md={4} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>Balance</div>
            <div className={`fs-2 fw-bold ${lowBalance ? 'text-danger' : ''}`}>{money(usage?.balance)}</div>
            {lowBalance && <span className='badge badge-light-danger'>Low balance</span>}
          </Card.Body></Card>
        </Col>
      </Row>

      <Row className='g-4'>
        <Col lg={5}>
          <Card className='h-100'>
            <Card.Header className='flex-column align-items-start justify-content-center'>
              <Card.Title className='mb-1'>Monthly spend</Card.Title>
              <span className='text-muted fs-8'>Click a month to see its breakdown →</span>
            </Card.Header>
            <Card.Body>
              {!usage ? <Spinner animation='border' size='sm' /> : (usage.trend || []).map((t) => {
                const active = t.month === activeKey
                return (
                  <div
                    key={t.month}
                    role='button'
                    onClick={() => selectMonth(t.month)}
                    title={`Show ${formatMonth(t.month)} breakdown`}
                    className={`d-flex align-items-center mb-3 p-1 rounded ${active ? 'bg-light-primary' : ''}`}
                    style={{cursor: 'pointer'}}
                  >
                    <span className={`fs-8 ${active ? 'text-primary fw-bold' : 'text-muted'}`} style={{width: 64}}>{t.month}</span>
                    <div className='flex-grow-1 mx-3 bg-light rounded' style={{height: 14}}>
                      <div className={`rounded ${active ? 'bg-primary' : 'bg-primary bg-opacity-50'}`} style={{height: 14, width: `${(t.total / trendMax) * 100}%`}} />
                    </div>
                    <span className='fw-semibold fs-7' style={{width: 80, textAlign: 'right'}}>{money(t.total)}</span>
                  </div>
                )
              })}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className='h-100'>
            <Card.Header className='flex-column align-items-start justify-content-center'>
              <div className='d-flex align-items-center justify-content-between w-100 mb-1'>
                <div className='d-flex align-items-center gap-2'>
                  <Card.Title className='mb-0'>{formatMonth(activeKey)} by category</Card.Title>
                  {selMonth && (
                    <button
                      type='button'
                      className='btn btn-link p-0 fs-8 text-primary'
                      onClick={() => setSelMonth(null)}
                    >
                      ← Back to this month
                    </button>
                  )}
                </div>
                <Button
                  variant='light-primary'
                  size='sm'
                  onClick={exportPdf}
                  disabled={activeLoading || activeCats.length === 0}
                >
                  Download PDF
                </Button>
              </div>
              <span className='text-muted fs-8'>
                Each category is shown at Twilio's standard (list) price. Your account discount is
                applied to the subtotal, so the <b>Total</b> is what Twilio actually bills.
              </span>
            </Card.Header>
            <Card.Body className='p-0'>
              {activeLoading ? (
                <div className='p-6'><Spinner animation='border' size='sm' /></div>
              ) : (() => {
                const lineItems = activeCats.filter((c) => c.kind !== 'adjustment')
                const adjustment = activeCats.find((c) => c.kind === 'adjustment')
                const listSubtotal = lineItems.reduce((s, c) => s + c.price, 0)
                return (
                  <div className='table-responsive'>
                    <table className='table table-row-dashed align-middle gy-2 mb-0'>
                      <thead>
                        <tr className='fw-bold text-muted'>
                          <th className='ps-6'>Category</th>
                          <th>What it is</th>
                          <th>Usage</th>
                          <th className='text-end pe-6'>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((c) => (
                          <tr key={c.category} className={c.kind === 'other' ? 'text-muted fst-italic' : ''}>
                            <td className='ps-6 fw-semibold'>{c.description || c.category}</td>
                            <td className='text-muted fs-8'>{CATEGORY_HELP[c.category] || ''}</td>
                            <td className='text-muted text-nowrap'>{c.usage} {c.usage_unit}</td>
                            <td className='text-end pe-6 fw-semibold'>{money(c.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {activeCats.length > 0 && (
                        <tfoot>
                          <tr className='text-muted border-top'>
                            <td className='ps-6'>Subtotal</td>
                            <td className='fs-8'>Usage at Twilio list price</td>
                            <td></td>
                            <td className='text-end pe-6'>{money(listSubtotal)}</td>
                          </tr>
                          {adjustment && (
                            <tr className='text-success'>
                              <td className='ps-6'>Discount &amp; adjustments</td>
                              <td className='fs-8'>Your Twilio account discount (not itemized by Twilio)</td>
                              <td></td>
                              <td className='text-end pe-6'>{money(adjustment.price)}</td>
                            </tr>
                          )}
                          <tr className='fw-bolder fs-5 border-top'>
                            <td className='ps-6'>Total billed</td>
                            <td className='text-muted fs-8 fw-normal'>What Twilio actually charges</td>
                            <td></td>
                            <td className='text-end pe-6'>{money(activeTotal)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )
              })()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
